import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const options = [
  "original",
  "parts_tracking",
  "damage_analysis",
  "combined_analysis",
];

const checkScreenSize = () => {
  return window.innerWidth >= 568;
};

export default function MultiVideoModal({ option, onClose }) {
  const [videoGroupIndex, setVideoGroupIndex] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showModal, setShowModal] = useState(true);
  const [startY, setStartY] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLargeScreen, setIsLargeScreen] = useState(checkScreenSize());
  const videoRef = useRef(null);
  const { t, i18n } = useTranslation();

  let sampleVideoGroups = [];

  if (option === "photogrammetry") {
    sampleVideoGroups = [
      ["/video/photogrammetry1.mp4"],
      ["/video/photogrammetry2.mp4"],
    ];
  } else {
    sampleVideoGroups = [
      [
        "/video/maruti1.mp4",
        "/video/maruti1Parts1.mp4",
        "/video/maruti1Damage1.mp4",
        "/video/maruti1Combined1.mp4",
      ],
      [
        "/video/maruti2.mp4",
        "/video/maruti2Parts2.mp4",
        "/video/maruti2Damage2.mp4",
        "/video/maruti2Combined2.mp4",
      ],
    ];
  }

  const videos = sampleVideoGroups[videoGroupIndex] || [];

  const handleTouchStart = (e) => {
    if (!isLargeScreen) {
      setStartY(e.touches[0].clientY);
    }
  };

  const handleTouchEnd = (e) => {
    if (!isLargeScreen && startY !== null) {
      const endY = e.changedTouches[0].clientY;
      const deltaY = startY - endY;

      if (deltaY > 50) {
        handleNext();
      } else if (deltaY < -50) {
        handleClose();
      }

      setStartY(null);
    }
  };

  const handleNext = () => {
    const nextIndex = (videoGroupIndex + 1) % sampleVideoGroups.length;
    setVideoGroupIndex(nextIndex);
    setSelectedVideo(null);
  };

  const handlePrev = () => {
    const prevIndex = videoGroupIndex === 0 ? sampleVideoGroups.length - 1 : videoGroupIndex - 1;
    setVideoGroupIndex(prevIndex);
    setSelectedVideo(null);
  };

  const handleClose = () => {
    onClose();
    setShowModal(false);
    setSelectedVideo(null);
    setVideoGroupIndex(0);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(checkScreenSize());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch((err) => {
        console.warn("Autoplay failed:", err);
      });
    }
  }, [selectedVideo]);

  useEffect(() => {
    const currentGroup = sampleVideoGroups[videoGroupIndex];
    if (currentGroup && currentGroup.length > 0) {
      setSelectedVideo(currentGroup[0]);
    }
  }, [videoGroupIndex]);

  useEffect(() => {
    const initialGroup = sampleVideoGroups[0];
    if (initialGroup && initialGroup.length > 0) {
      setSelectedVideo(initialGroup[0]);
    }
  }, []);

  if (!showModal) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black text-white flex flex-col items-center justify-center p-4"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="w-full h-full flex flex-col justify-center items-center">
        <div className="px-2 py-1 text-xs md:text-lg text-center">
          {option === "photogrammetry"
            ? `${t("photogrammetry")} : ${videoGroupIndex + 1}`
            : `${t("video_assessment")} : ${videoGroupIndex + 1}`}
        </div>
        <div className={`w-full aspect-video ${isLargeScreen ? 'max-w-2xl' : 'max-w-sm'} rounded-xl overflow-hidden bg-black shadow-lg relative`}>
          {/* Close button for large screens - positioned above video */}
          {isLargeScreen && (
            <button
              onClick={handleClose}
              className="absolute top-2  right-2 z-10 bg-gray-800 hover:bg-gray-700 rounded-full p-2 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          {selectedVideo ? (
            <video
              ref={videoRef}
              key={selectedVideo}
              src={selectedVideo}
              controls
              autoPlay
              muted
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              Select a video
            </div>
          )}
        </div>

        {videos.length > 1 && (
          <div
            className={`mt-6 grid ${
              videos.length <= 2 ? "grid-cols-1" : "grid-cols-2"
            } gap-3 w-full ${isLargeScreen ? 'max-w-2xl' : 'max-w-sm'}`}
          >
            {videos.map((video, index) => (
              <button
                key={index}
                onClick={() => {
                  setSelectedVideo(video);
                  setCurrentIndex(index);
                }}
                className={`rounded-xl py-2 px-3 text-sm font-semibold transition ${
                  index === currentIndex
                    ? "bg-blue-500 text-white"
                    : "bg-white text-black"
                }`}
              >
                {t(options[index])}
              </button>
            ))}
          </div>
        )}

        {/* Conditional rendering based on screen size */}
        {isLargeScreen ? (
          <div className="mt-6 flex items-center gap-4">
            <button
              onClick={handlePrev}
              className="bg-gray-800 hover:bg-gray-700 rounded-full p-3 transition-colors flex items-center justify-center"
              aria-label="Previous"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <div className="text-center">
              <span className="text-sm text-gray-300">
                {videoGroupIndex + 1} / {sampleVideoGroups.length}
              </span>
            </div>

            <button
              onClick={handleNext}
              className="bg-gray-800 hover:bg-gray-700 rounded-full p-3 transition-colors flex items-center justify-center"
              aria-label="Next"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        ) : (
          <p className="text-xs mt-6 text-gray-400 text-center">
            {t("swipe_up")}
            <br />
            {t("swipe_down")}
          </p>
        )}
      </div>
    </div>
  );
}